import { Phone } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Table, type Column } from '@/components/ui/Table';
import { cn } from '@/lib/utils';
import type { TransportRoute } from '@/types/transport';
import { ROUTE_STATUS, ROUTE_TONE } from '../constants';
import { ROWS_PER_PAGE, useRegister } from '../useRegister';
import { RegisterToolbar } from './RegisterToolbar';
import { RouteCard } from './RouteCard';
import { RowActions } from './RowActions';

/** What a status in the address may be — every one this register's rows can hold. */
const ROUTE_STATUSES = Object.keys(ROUTE_STATUS);

/** How many routes are drawn as cards before the list below takes over. */
const CARDS_SHOWN = 4;

export interface RoutesTabProps {
  routes: TransportRoute[];
  onOpenRoute?: (route: TransportRoute) => void;
  onEditRoute?: (route: TransportRoute) => void;
  onRouteMenu?: (route: TransportRoute) => void;
}

/**
 * Every route the institute runs — the first few as cards, then all of them as
 * a list.
 *
 * The cards are not a different set of routes from the list; they are the first
 * of the same set, drawn large because a route is a shape and a distance before
 * it is a row. The list underneath is what somebody actually works through, so
 * it carries the search, the filter and the paging.
 *
 * Both read the props they were handed, so search and filter work before any
 * endpoint exists. When the register goes live the filtering moves to the
 * server and this component keeps its shape.
 */
export function RoutesTab({ routes, onOpenRoute, onEditRoute, onRouteMenu }: RoutesTabProps) {
  const register = useRegister({
    prefix: 'routes',
    statuses: ROUTE_STATUSES,
    rows: routes,
    fieldsOf: (route) => [route.code, route.name, route.driverName, route.vehicleNumber],
    statusOf: (route) => route.status,
  });

  const columns: Column<TransportRoute>[] = [
    {
      key: 'code',
      header: 'Route no.',
      sticky: 'left',
      stickyWidth: 110,
      render: (route) => (
        <span className={cn('pill text-[12px] font-bold tracking-label', ROUTE_TONE[route.tone].soft)}>
          {route.code}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Route name',
      render: (route) => <span className="font-bold text-ink">{route.name}</span>,
    },
    {
      key: 'distance',
      header: 'Distance',
      render: (route) => <span className="font-semibold text-ink-2">{route.distanceKm} km</span>,
    },
    {
      key: 'duration',
      header: 'Duration',
      hideOnMobile: true,
      render: (route) => (
        <span className="font-semibold text-ink-2">{route.durationMinutes} min</span>
      ),
    },
    {
      key: 'stops',
      header: 'Stops',
      align: 'center',
      hideOnMobile: true,
      render: (route) => <span className="font-semibold text-ink-2">{route.stops}</span>,
    },
    {
      key: 'students',
      header: 'Students',
      align: 'center',
      render: (route) => (
        <span className="font-semibold text-ink-2">
          {route.studentsAssigned}
          <span className="text-ink-3"> / {route.capacity}</span>
        </span>
      ),
    },
    {
      key: 'vehicle',
      header: 'Vehicle',
      hideOnMobile: true,
      render: (route) => (
        <span className="font-semibold text-ink-2">{route.vehicleNumber ?? '—'}</span>
      ),
    },
    {
      key: 'driver',
      header: 'Driver',
      hideOnMobile: true,
      render: (route) =>
        route.driverName ? (
          <div className="min-w-0">
            <p className="truncate font-bold text-ink">{route.driverName}</p>
            <p className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-ink-3">
              <Phone className="h-3 w-3" aria-hidden />
              9876543210
            </p>
          </div>
        ) : (
          <span className="font-semibold text-ink-3">Not assigned</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (route) => (
        <Badge tone={ROUTE_STATUS[route.status].tone} withDot>
          {ROUTE_STATUS[route.status].label}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      sticky: 'right',
      stickyWidth: 90,
      render: (route) => (
        <RowActions
          subject={route.name}
          onOpen={onOpenRoute && (() => onOpenRoute(route))}
          onEdit={onEditRoute && (() => onEditRoute(route))}
          onMore={onRouteMenu && (() => onRouteMenu(route))}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      {/* The cards, with the section's own search and filter above them. */}
      <section className="flex flex-col gap-4">
        <RegisterToolbar
          title="All routes"
          searchPlaceholder="Search routes…"
          register={register}
          statusOptions={[
            { value: 'ACTIVE', label: ROUTE_STATUS.ACTIVE.label },
            { value: 'SUSPENDED', label: ROUTE_STATUS.SUSPENDED.label },
          ]}
        />

        {register.visible.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 w-full max-w-full">
            {register.visible.slice(0, CARDS_SHOWN).map((route, index) => (
              <div
                key={route.uuid}
                className="animate-fade-up"
                style={{ animationDelay: `${Math.min(index, 4) * 60}ms` }}
              >
                <RouteCard route={route} onOpen={onOpenRoute} onMenu={onRouteMenu} />
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-line bg-surface p-4 shadow-sm">
            <EmptyState
              title="No routes match"
              description="Nothing matches that search or filter. Try another name, code or status."
            />
          </div>
        )}
      </section>

      {/* The whole register, paged. */}
      <section className="rounded-3xl border border-line bg-surface shadow-sm">
        <header className="border-b border-line px-4 py-3.5">
          <h2 className="text-base font-bold text-ink">Routes list</h2>
          <p className="mt-0.5 text-sm text-ink-2">
            Every route, what runs it and how many children it carries.
          </p>
        </header>

        <Table
          columns={columns}
          rows={register.page}
          rowKey={(route) => route.uuid}
          variant="plain"
          empty={
            <EmptyState
              title="No routes"
              description="Routes appear here once the transport register has been set up."
            />
          }
        />

        {register.visible.length > 0 && (
          <Pagination
            meta={register.meta}
            onPageChange={register.goToPage}
            itemLabel="routes"
            showPageNumbers
            limitOptions={ROWS_PER_PAGE}
            onLimitChange={register.setLimit}
          />
        )}
      </section>
    </div>
  );
}
